import type { DownloadLinks, PlatformId } from "../data/siteContent";

export type OsId = "windows" | "macos" | "linux" | "unknown";
export type CpuArch = "x64" | "arm64" | "unknown";

export type NavigatorHints = {
  userAgent: string;
  maxTouchPoints?: number;
  /** From Client Hints `architecture` when available (e.g. "arm", "x86") */
  uaArchitecture?: string | null;
};

type NavigatorUAData = {
  getHighEntropyValues: (
    hints: string[],
  ) => Promise<{ architecture?: string; bitness?: string }>;
};

export function detectOs(userAgent: string): OsId {
  const ua = userAgent.toLowerCase();
  if (ua.includes("win")) return "windows";
  if (ua.includes("mac")) return "macos";
  if (ua.includes("linux")) return "linux";
  return "unknown";
}

/** Map Client Hints / UA-CH architecture token to CpuArch */
export function parseUaDataArchitecture(
  architecture: string | null | undefined,
): CpuArch {
  if (!architecture) return "unknown";
  const arch = architecture.toLowerCase();
  if (arch === "arm" || arch.includes("arm64") || arch.includes("aarch64")) {
    return "arm64";
  }
  if (
    arch === "x86" ||
    arch.includes("x86_64") ||
    arch.includes("amd64") ||
    arch.includes("x64")
  ) {
    return "x64";
  }
  return "unknown";
}

/**
 * Best-effort CPU arch from UA / Client Hints.
 * macOS Apple Silicon often masquerades as "Intel Mac OS X" in the UA string —
 * prefer uaArchitecture or maxTouchPoints; otherwise return unknown (do not guess Intel).
 */
export function detectCpuArch(
  userAgent: string,
  maxTouchPoints = 0,
  uaArchitecture?: string | null,
): CpuArch {
  const fromHints = parseUaDataArchitecture(uaArchitecture);
  if (fromHints !== "unknown") return fromHints;

  const ua = userAgent.toLowerCase();
  if (ua.includes("aarch64") || ua.includes("arm64")) return "arm64";
  if (ua.includes("x86_64") || ua.includes("amd64") || ua.includes("win64")) {
    return "x64";
  }
  if (/armv\d+/u.test(ua)) return "arm64";

  if (ua.includes("mac")) {
    // Safari on Apple Silicon often reports maxTouchPoints > 1
    if (maxTouchPoints > 1) return "arm64";
    return "unknown";
  }

  return "unknown";
}

/** Resolve arch using navigator.userAgentData when the browser exposes it */
export async function detectCpuArchAsync(
  hints: NavigatorHints,
): Promise<CpuArch> {
  const sync = detectCpuArch(
    hints.userAgent,
    hints.maxTouchPoints ?? 0,
    hints.uaArchitecture,
  );
  if (sync !== "unknown") return sync;

  if (typeof navigator === "undefined") return sync;

  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData })
    .userAgentData;
  if (!uaData?.getHighEntropyValues) return sync;

  try {
    const values = await uaData.getHighEntropyValues(["architecture"]);
    const fromUaData = parseUaDataArchitecture(values.architecture);
    if (fromUaData !== "unknown") return fromUaData;
  } catch {
    // Permission / unsupported — keep sync result
  }

  return sync;
}

function macDownloadHref(arch: CpuArch, links: DownloadLinks): string {
  // Prefer Apple Silicon when unsure — majority of current Macs; Intel remains selectable in UI
  return arch === "x64" ? links.macos.x64 : links.macos.arm64;
}

function linuxDownloadHref(arch: CpuArch, links: DownloadLinks): string {
  return arch === "arm64" ? links.linux.arm64 : links.linux.x64;
}

export function resolveDownloadHref(
  os: OsId,
  links: DownloadLinks,
  hints: NavigatorHints,
): string {
  const arch = detectCpuArch(
    hints.userAgent,
    hints.maxTouchPoints ?? 0,
    hints.uaArchitecture,
  );

  if (os === "windows") return links.windows;
  if (os === "macos") return macDownloadHref(arch, links);
  if (os === "linux") return linuxDownloadHref(arch, links);
  return links.windows;
}

/** Per-platform card button: picks arch when visitor is on that OS, else sensible default */
export function resolvePlatformDownloadHref(
  platform: PlatformId,
  links: DownloadLinks,
  hints: NavigatorHints,
): string {
  if (platform === "windows") return links.windows;

  const os = detectOs(hints.userAgent);
  const arch = detectCpuArch(
    hints.userAgent,
    hints.maxTouchPoints ?? 0,
    hints.uaArchitecture,
  );

  if (platform === "macos") {
    if (os === "macos") return macDownloadHref(arch, links);
    return links.macos.arm64;
  }

  if (os === "linux") return linuxDownloadHref(arch, links);
  return links.linux.x64;
}
